import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { useNotification } from "@/shared/components/notifications/useNotification";
import { type FileRecord } from "@/shared/domain/files";
import {
  DEFAULT_VISUALIZATION_ASSET_SETTINGS,
  ingestPhase,
  isPointCloud,
  type VisualizationAssetSettings,
} from "@/shared/domain/urdf";
import {
  useRequestVisualizationAsset,
  useResetVisualizationAsset,
  useVisualizationAsset,
  useVisualizationAssetFile,
} from "@/shared/domain/urdf/hooks";
import { Button } from "@/shared/foundation/primitives";

import { ConvertingStep } from "./ConvertingStep";
import styles from "./IngestAsAssetDialog.module.css";
import { PreviewStep } from "./PreviewStep";
import { SettingsStep } from "./SettingsStep";
import { useJointBindingDraft } from "./useJointBindingDraft";

interface IngestAsAssetDialogProps {
  /** The `.urdf` or `.ply` to ingest. `null` closes the dialog. */
  file: FileRecord | null;
  onClose: () => void;
  /**
   * Called once the wizard has been left, when it leaves an asset behind.
   *
   * Only for callers that opened the wizard *in order to* get an asset — the
   * model picker sends a file with nothing ingested through here and then has
   * to finish the pick it started. The file browser opens the wizard as an end
   * in itself and passes nothing.
   *
   * Fires on the way out rather than the moment the conversion lands, because
   * a caller that acted on it mid-flight would tear the wizard off the screen
   * while the user is still in it — before they have looked at the robot, let
   * alone declared a binding. It does not fire at all for a wizard that never
   * reached a finished asset, so cancelling out of the settings form is the
   * abandonment it looks like.
   *
   * @param source the file the wizard was opened on, never the artifact
   * @param assetRelativePath where the artifact is, when the status block named
   *   it — the caller's own copy of the record predates the conversion and
   *   cannot say
   */
  onIngested?: (source: FileRecord, assetRelativePath?: string) => void;
}

/**
 * Converts a robot description into a viewable asset, and shows the result.
 *
 * Four things it can be showing — reading the file's status, the settings form,
 * a conversion in flight, the finished robot — and **which one is derived from
 * server state**, never from local step state. The source file's
 * `roboto/visualization_asset` metadata says where the work has got to, so a
 * conversion that was already running (or already finished) when the dialog
 * opened puts the user straight where it is, and a reload cannot desynchronise
 * the wizard from the platform. A block describing a *previous upload* of the
 * file is ignored, so a re-uploaded URDF is offered a fresh conversion rather
 * than the old upload's robot — see `ingestPhase`.
 *
 * There is no step indicator over the top of them. Three states, one of which
 * the user usually never sees, is not a journey worth narrating: the dialog
 * shows whichever state it is in and nothing about the ones it is not in. The
 * states themselves are not collapsed — a conversion is a real wait on a
 * worker, and a form left up through it would read as a form that ignored its
 * own button.
 *
 * The dialog is sized to the state it is in rather than to the largest of them:
 * three of the four are a short column of controls and take a small paper, and
 * only the preview — a 3D viewport with a sidebar — takes the wide one. See
 * `isPreview` below.
 *
 * Held open by whichever file it is given, so there is one instance for the
 * whole browser rather than one per row — the same arrangement as the existing
 * confirmation dialog.
 */
export function IngestAsAssetDialog({
  file,
  onClose,
  onIngested,
}: IngestAsAssetDialogProps) {
  const [settings, setSettings] = React.useState<VisualizationAssetSettings>(
    DEFAULT_VISUALIZATION_ASSET_SETTINGS,
  );

  /**
   * The file the dialog is *showing*, as opposed to the one it has been given.
   *
   * The two are the same on the way in, and differ for the length of the exit
   * transition on the way out. `onClose` clears the caller's state
   * synchronously, so `file` becomes `null` on the click — while MUI is still
   * playing a ~200ms fade of the dialog the user is looking at. Everything below
   * is derived from the file, so following it straight down would empty the
   * dialog *during* that fade: the robot vanishes, the phase falls back to the
   * unknown, and the paper shrinks to the small size, all of it visible before
   * the box has finished disappearing.
   *
   * So the *dismissal* follows the prop — `open` below is `file !== null`, and
   * pressing Cancel still starts the close immediately — and the *teardown*
   * follows this, which is released in `resetState` once `onExited` has fired.
   * Updated during render rather than in an effect, which is React's documented
   * way to adjust state to a prop: an effect would commit one frame of the wrong
   * file first, which is the flash this exists to prevent.
   *
   * What it deliberately does **not** cover is the two mutations below, which
   * stay on `file`. Those act on the dialog the user has open, and a dialog that
   * is already closing has nothing left to convert or reset.
   */
  const [shownFile, setShownFile] = React.useState<FileRecord | null>(file);
  if (file !== null && file !== shownFile) {
    setShownFile(file);
  }

  const request = useRequestVisualizationAsset();
  const assetQuery = useVisualizationAsset(shownFile ?? undefined, {
    enabled: shownFile !== null,
  });
  const block = assetQuery.data ?? null;

  /**
   * Where the wizard is, or `undefined` while the file's record is still being
   * read.
   *
   * A pending read is not the same answer as an absent block, though
   * `assetQuery.data` is `undefined` for both: `ingestPhase(null, …)` reads as
   * `settings`, so a phase derived before the read lands opens an
   * already-converted robot on a settings form — with a live **Convert**
   * button, which a user quick enough to press runs the entire conversion again
   * — and then jumps to the preview a round trip later. The unknown is
   * therefore rendered as an unknown, and the states below are what the server
   * has actually said.
   */
  const phase =
    shownFile === null || assetQuery.isPending
      ? undefined
      : ingestPhase(block, shownFile.upload_id);

  // Resolved only on the preview, and from the block's path when it names
  // one — the canonical path derived from the source record is the fallback, so
  // an artifact is addressable even from a block that names nothing.
  const assetFileQuery = useVisualizationAssetFile(
    shownFile ?? undefined,
    block?.relative_path,
    phase === "preview",
  );
  const assetFile = assetFileQuery.data ?? null;

  /**
   * Whether the dialog is showing the one state that needs the room.
   *
   * The preview is a Babylon viewport with a fixed 20rem sidebar beside it, and
   * anything narrower leaves the canvas a strip. The other three states — the
   * status read, the settings form, the conversion — are a short column of text
   * and controls that a `lg` paper simply surrounds with empty space, so they
   * take `sm` and their own content's height.
   *
   * The size is derived from the same `phase` the body is, and changes in the
   * same render, so a state and the space it is given can never disagree. It is
   * deliberately not four sizes: the wizard passes through the status read on
   * *every* open and through the conversion on every fresh one, and giving those
   * a size of their own would resize the dialog under the user twice on the way
   * to a form that had not changed.
   *
   * The growth is not animated. `height` cannot be transitioned from `auto`, so
   * a transition here would animate the width while the height snapped, and the
   * body is a different component either side of the change — a frame easing
   * around wholly replaced content reads as lag rather than as growth. (The
   * canvas would survive one: `BabylonManager` keeps a `ResizeObserver` on it.
   * But it does not mount until the glTF has been fetched and parsed, which is
   * well after the paper has settled, so it never measures a moving container.)
   */
  const isPreview = phase === "preview";

  /**
   * The finished asset the wizard has shown, held until it has finished
   * leaving.
   *
   * Written whenever the preview is on screen — the phase is derived from the
   * server, so reaching it means the platform has an artifact for this upload,
   * whether this visit built it or found it already there. Read once from
   * {@link resetState}, which is the only place {@link onIngested} fires.
   */
  const ingested = React.useRef<
    { source: FileRecord; assetRelativePath?: string } | undefined
  >(undefined);
  const assetRelativePath = block?.relative_path;
  React.useEffect(
    function rememberWhateverTheWizardHasBuilt() {
      // Cleared as readily as it is set. Reset asset destroys the artifact and
      // drops the wizard back to its settings form, and a caller told about an
      // ingest at that point would go looking for a file that has just been
      // deleted.
      ingested.current =
        isPreview && shownFile !== null
          ? { source: shownFile, assetRelativePath }
          : undefined;
    },
    [isPreview, shownFile, assetRelativePath],
  );

  /**
   * Whether the shown source is a point cloud scan rather than a robot
   * description. Drives every surface that words or gates itself per source
   * kind: the settings form, the converting copy, the preview's joints
   * section and Save action, and the reset confirmation.
   */
  const pointcloud = shownFile !== null && isPointCloud(shownFile.name);

  const reset = useResetVisualizationAsset();
  const [confirmingReset, setConfirmingReset] = React.useState(false);

  /**
   * The joint declarations the preview is editing, held here rather than in the
   * step that shows them, because the button that commits them is in this
   * dialog's action row. Everything it holds is unsaved by definition, and
   * {@link resetState} drops it — which is what makes Cancel a discard rather
   * than a mere dismissal.
   *
   * Addressed by `file` rather than by {@link shownFile}, alongside the other
   * two mutations and for the same reason: a dialog that is already closing has
   * nothing left to save.
   */
  const draft = useJointBindingDraft(file ?? undefined);
  const notify = useNotification();

  const startConversion = () => {
    if (file === null) {
      return;
    }
    request.mutate({ file, settings });
  };

  /**
   * Destroys the generated asset, putting the wizard back on its settings form.
   *
   * Awaited rather than fired, so the confirmation stays up — with its button in
   * a loading state — until the deletes and the metadata write have both
   * settled. A destructive action that dismisses its own confirmation before it
   * has done anything reads as having done nothing.
   *
   * The failure is swallowed rather than rethrown: it is shown from the
   * mutation's own error state in the action row, which outlives the
   * confirmation, and an exception escaping a click handler would go nowhere a
   * user can see.
   */
  const resetAsset = async () => {
    if (file === null) {
      return;
    }
    try {
      await reset.mutateAsync({ file, relativePath: block?.relative_path });
      // The asset the declarations were read off — and the one a save would
      // write them back into — has just been destroyed, so the draft describes
      // nothing. Left standing, it would be laid over the *next* conversion's
      // model, quietly restoring bindings the user was told had gone with the
      // asset.
      draft.clear();
    } catch {
      // Reported by `reset.isError` below.
    } finally {
      setConfirmingReset(false);
    }
  };

  /**
   * Commits the declarations, and leaves.
   *
   * Save dismisses because that is the only reading of a Save/Cancel pair that
   * stays true: with the dialog gone there is never a Cancel sitting beside a
   * save that has already been committed, offering an undo it cannot perform.
   * Bindings are written by a worker into an artifact other people read, and
   * nothing here can call that back.
   *
   * It is awaited rather than fired, so a refusal is reported into a dialog that
   * is still on screen to report it — the failure keeps the wizard up, with the
   * message in the action row and the user's edits still in the rows. Only
   * acceptance closes.
   *
   * What acceptance means is narrower than "the asset carries these bindings":
   * the platform has *queued* the write, and the artifact catches up seconds
   * later. That is the fact the notification carries out with the user, since
   * the dialog it used to be shown in has by then gone.
   */
  const saveBindings = async () => {
    if (!(await draft.save())) {
      return;
    }
    notify(
      "Joint bindings saved. They are written into the generated asset in the " +
        "background, so reopening this dialog in a few seconds shows them.",
      "success",
      6000,
    );
    onClose();
  };

  /**
   * Everything the dialog was holding, dropped once it has finished leaving.
   *
   * Runs from `onExited`, so it is also what releases {@link shownFile} — and
   * with it the polling queries keyed on that file. Held until here rather than
   * cleared on the click, because the dialog is still on screen for the length
   * of the exit and the user is still looking at what it held.
   *
   * This is where Cancel's promise is actually kept. Every unsaved joint
   * declaration lives in {@link draft}, so dropping it here is what makes
   * leaving the wizard — by the button, by Escape, or by the backdrop, all of
   * which are this same close — an abandonment of the edits rather than a
   * dismissal that quietly keeps them. Only the unsaved ones: a save the
   * platform has already accepted is in the artifact's future, not in this
   * state, and re-opening reads it back off the model.
   */
  const resetState = () => {
    // Reported from here rather than from the click, so the caller acts on a
    // wizard that has actually gone: the model picker's next act is to resolve
    // the artifact and close itself, which would otherwise happen underneath a
    // dialog still fading out.
    const finished = ingested.current;
    ingested.current = undefined;
    if (finished !== undefined) {
      onIngested?.(finished.source, finished.assetRelativePath);
    }
    setShownFile(null);
    setSettings(DEFAULT_VISUALIZATION_ASSET_SETTINGS);
    request.reset();
    reset.reset();
    draft.clear();
    setConfirmingReset(false);
  };

  return (
    <Dialog
      PaperProps={{
        className: classNames({ [styles.previewPaper]: isPreview }),
      }}
      TransitionProps={{ onExited: resetState }}
      fullWidth
      // `xl` is the preview's own width, chosen for the viewport; `sm` (600px)
      // is what the three short states need — wide enough that a switch's label
      // sits on one line and the packages field is usable, narrow enough that a
      // five-control form is not marooned in the middle of a widescreen.
      maxWidth={isPreview ? "xl" : "sm"}
      onClose={onClose}
      open={file !== null}
    >
      <DialogTitle className={styles.title}>
        Ingest {shownFile?.name ?? "file"} as an asset
      </DialogTitle>
      <DialogContent className={styles.content}>
        <div className={styles.stateBody}>
          {phase === undefined && (
            <div className={styles.pendingPhase}>
              <CircularProgress size="1.5rem" />
              <span>Looking for an existing asset…</span>
            </div>
          )}
          {phase === "settings" && (
            <SettingsStep
              error={request.error?.message}
              onChange={setSettings}
              pointcloud={pointcloud}
              settings={settings}
            />
          )}
          {(phase === "converting" || phase === "failed") && (
            <ConvertingStep
              block={block}
              failed={phase === "failed"}
              sourceNoun={pointcloud ? "point cloud" : "robot description"}
            />
          )}
          {phase === "preview" && (
            <PreviewStep
              pointcloud={pointcloud}
              file={
                assetFile
                  ? {
                      fileId: assetFile.file_id,
                      relativePath: assetFile.relative_path,
                    }
                  : undefined
              }
              resolving={assetFileQuery.isPending}
              unresolved={block?.unresolved ?? []}
              // The URDF, not the artifact: the joint-bindings route is
              // addressed by the source file and derives the asset it patches.
              // Nothing is read off this record — what the robot already
              // declares is read out of the glTF the preview loaded.
              sourceFile={shownFile ?? undefined}
              bindings={draft.bindings}
              onBindingChange={draft.onBindingChange}
              onJointsLoaded={draft.onJointsLoaded}
            />
          )}
        </div>
      </DialogContent>
      {/*
        One shape for every state: whatever destroys, then the way out, then the
        one act that commits. The preview used to be the exception — its commit
        sat in the joints panel and the action row ended in a Done that only
        dismissed, so the button that finished the wizard and the button that
        saved the work were in different places and neither said which was
        which. Now it reads as the other three do.
      */}
      <DialogActions>
        {reset.isError && (
          <Alert className={styles.actionError} severity="error">
            The asset could not be reset: {reset.error.message}
          </Alert>
        )}
        {draft.error !== undefined && (
          <Alert className={styles.actionError} severity="error">
            The bindings could not be saved: {draft.error}
          </Alert>
        )}
        {/* Offered wherever a block exists to remove — after a conversion that
            worked, and after one that did not. `failed` also leaves a block, and
            possibly a half-written artifact, and starting over is exactly what a
            user reaches for there. */}
        {(phase === "preview" || phase === "failed") && (
          <Button
            color="error"
            loading={reset.isPending}
            onClick={() => setConfirmingReset(true)}
          >
            Reset asset
          </Button>
        )}
        {/* The same word in every state, and the same act: close, keeping
            nothing that has not been sent. It is deliberately not renamed to
            "Close" on the preview once a save has landed — a save closes the
            dialog, so there is no state in which this button stands beside
            committed work and could be read as offering to undo it.

            It asks nothing first, even with edits pending. What it discards is
            a handful of paths typed into rows that are still on screen, against
            a model that is still there to type them into again; a confirmation
            over that would tax every uneventful dismissal to save the rare one.
            Reset asset does ask, because what *it* destroys — the artifact, and
            the bindings stored inside it — cannot be typed back. */}
        <Button onClick={onClose}>Cancel</Button>
        {phase === "settings" && (
          <Button
            loading={request.isPending}
            onClick={startConversion}
            variant="contained"
          >
            Convert
          </Button>
        )}
        {phase === "failed" && (
          <Button
            loading={request.isPending}
            onClick={startConversion}
            variant="contained"
          >
            Retry
          </Button>
        )}
        {/* One save for the whole robot, because the route replaces the
            complete set: saving per row would make each one a write that
            clears the others. Not offered for a point cloud: the asset has no
            joints, so there is nothing to commit — and the joint-bindings
            route refuses non-URDF sources, so the button's only possible
            outcome would be an error. */}
        {phase === "preview" && !pointcloud && (
          <Button
            loading={draft.isSaving}
            onClick={() => void saveBindings()}
            variant="contained"
          >
            Save
          </Button>
        )}
      </DialogActions>
      {/*
        The wizard's own confirmation rather than the file browser's shared
        `AlertDialog`. That component pins itself to `zLayer(10)` — 999 — which
        is above the visualization workspace it was written for and *below* MUI's
        own dialog layer at 1300, so raised from inside this modal it renders
        behind this modal's backdrop: visible, and impossible to click. Two plain
        dialogs stack by mount order instead, which is the behaviour wanted here.
      */}
      <Dialog
        maxWidth="sm"
        onClose={() => setConfirmingReset(false)}
        open={confirmingReset}
      >
        <DialogTitle className={styles.title}>Reset asset?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This deletes the generated 3D asset for{" "}
            {shownFile?.name ?? "this file"}, so the{" "}
            {pointcloud ? "point cloud" : "robot description"} has to be
            converted again from scratch. It cannot be undone, and re-converting
            a large {pointcloud ? "scan" : "robot"} takes a while.
          </DialogContentText>
          {/* Names the second thing it destroys, which is the one the user is
              least likely to expect. Bindings live in the asset's own
              `ROBOTO_device` extension and nowhere else, so deleting the asset
              deletes them — and a destructive confirmation that misstates what
              it destroys is worse than no confirmation at all. A point cloud's
              asset never held bindings, so for one this paragraph would warn
              about destroying data that does not exist; it is omitted. */}
          {!pointcloud && (
            <DialogContentText className={styles.resetKeeps}>
              Your saved joint bindings go with it. They are stored in the
              generated asset itself, so they will have to be declared again
              after the next conversion.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingReset(false)}>Cancel</Button>
          {/* Names the deed rather than echoing the trigger, so the
              confirmation says what pressing it does instead of asking the
              same question twice. */}
          <Button
            color="error"
            loading={reset.isPending}
            onClick={() => void resetAsset()}
            variant="contained"
          >
            Delete the asset
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
